// src/pages/LoginPage.js
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "./LoginPage.css";

const LOGO_ICON_SRC = "/logo-icon.png";
const LOGO_TEXT_SRC = "/logo-text-black.png";

function EyeIcon({ open }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M2.5 12s3.5-7 9.5-7 9.5 7 9.5 7-3.5 7-9.5 7-9.5-7-9.5-7Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M12 15.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4Z"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      {open ? null : (
        <path
          d="M4 4l16 16"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
}

function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function isLikelyPin(v) {
  return /^\d{8}$/.test(String(v || "").trim());
}

function normalizePlatformRole(v) {
  const r = safeString(v).trim().toUpperCase();
  if (r === "SUPER_ADMIN" || r === "ADMIN" || r === "SUPPORT") return r;
  return "";
}

function normalizeStoreRole(v) {
  const r = safeString(v).trim().toLowerCase();
  if (r === "tech") return "technician";
  if (r === "service_manager") return "manager";
  if (r === "store_manager") return "manager";
  return r;
}

export function getStoreLandingPath(role) {
  const r = normalizeStoreRole(role);

  if (r === "advisor") return "/advisor";
  if (r === "parts") return "/parts-manager";
  if (r === "staff") return "/parts";
  if (r === "foreman") return "/shop-foreman";
  if (r === "technician") return "/tech";

  return "/dashboard";
}

export default function LoginPage() {
  const nav = useNavigate();
  const auth = useAuth();

  const [pin, setPin] = useState("");
  const [password, setPassword] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [showPw, setShowPw] = useState(false);

  const [fpOpen, setFpOpen] = useState(false);
  const [fpPin, setFpPin] = useState("");
  const [fpStatus, setFpStatus] = useState("");
  const [fpError, setFpError] = useState("");
  const [fpSending, setFpSending] = useState(false);

  const canSubmit = useMemo(() => {
    return isLikelyPin(pin) && !!password && !isSubmitting;
  }, [pin, password, isSubmitting]);

  async function onSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;

    setError("");
    setIsSubmitting(true);

    try {
      if (typeof auth?.login !== "function") {
        throw new Error("Auth login() not available.");
      }

      const data = await auth.login({
        pin: safeString(pin).trim(),
        password: safeString(password),
      });

      const nextPlatformRole = normalizePlatformRole(
        data?.user?.platformRole || auth?.user?.platformRole || auth?.platformRole
      );

      if (nextPlatformRole) {
        nav("/platform/admin", { replace: true });
        return;
      }

      const role =
        data?.stores?.[0]?.role ||
        data?.user?.role ||
        auth?.membership?.role ||
        auth?.user?.role ||
        "";

      nav(getStoreLandingPath(role), { replace: true });
    } catch (err) {
      setError(err?.message || "Invalid credentials.");
    } finally {
      setIsSubmitting(false);
    }
  }

  function openForgotPassword() {
    setFpOpen(true);
    setFpPin(isLikelyPin(pin) ? safeString(pin).trim() : "");
    setFpStatus("");
    setFpError("");
  }

  function closeForgotPassword() {
    if (fpSending) return;
    setFpOpen(false);
  }

  async function sendPasswordReset(e) {
    e.preventDefault();
    setFpStatus("");
    setFpError("");

    const p = safeString(fpPin).trim();
    if (!isLikelyPin(p)) {
      setFpError("Please enter your 8-digit PIN.");
      return;
    }

    try {
      setFpSending(true);
      if (typeof auth?.publicPost !== "function") {
        throw new Error("Auth publicPost() not available.");
      }

      await auth.publicPost("/api/auth/request-password-reset", { pin: p });
      setFpStatus("If that account exists, a reset link has been sent.");
    } catch (err) {
      setFpError(err?.message || "Could not send reset link.");
    } finally {
      setFpSending(false);
    }
  }

  return (
    <main className="ft-loginPage">
      <section className="ft-loginCard" aria-labelledby="login-title">
        <div className="ft-loginBrandWrap">
          <img
            src={LOGO_ICON_SRC}
            alt=""
            className="ft-loginLogoIcon"
            draggable={false}
            aria-hidden="true"
          />
          <img
            src={LOGO_TEXT_SRC}
            alt="Flowtrak"
            className="ft-loginLogoText"
            draggable={false}
          />
        </div>

        <h1 id="login-title" className="ft-srOnly">
          Sign in to Flowtrak
        </h1>

        {error ? (
          <div className="ft-loginError" role="alert" id="login-error">
            {error}
          </div>
        ) : null}

        <form
          onSubmit={onSubmit}
          className="ft-loginForm"
          aria-describedby={error ? "login-error" : undefined}
        >
          <div className="ft-loginField">
            <label className="ft-loginLabel" htmlFor="login-pin">
              PIN
            </label>
            <input
              id="login-pin"
              name="pin"
              className="ft-loginInput"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              inputMode="numeric"
              autoComplete="username"
              placeholder="8-digit PIN"
              required
              aria-required="true"
              aria-invalid={!!pin && !isLikelyPin(pin)}
              aria-label="8-digit Flowtrak PIN"
              title="Enter your 8-digit Flowtrak PIN"
            />
          </div>

          <div className="ft-loginField">
            <label className="ft-loginLabel" htmlFor="login-password">
              Password
            </label>

            <div className="ft-loginPwWrap">
              <input
                id="login-password"
                name="password"
                className="ft-loginInput ft-loginInput--pw"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type={showPw ? "text" : "password"}
                autoComplete="current-password"
                required
                aria-required="true"
              />
              <button
                type="button"
                className="ft-loginPwToggle"
                onClick={() => setShowPw((v) => !v)}
                aria-label={showPw ? "Hide password" : "Show password"}
                aria-pressed={showPw}
              >
                <EyeIcon open={showPw} />
              </button>
            </div>
          </div>

          <button type="submit" className="ft-loginBtn" disabled={!canSubmit}>
            {isSubmitting ? "Signing in…" : "Sign in"}
          </button>

          <div className="ft-loginHelpLinks" aria-label="Account help">
            <button type="button" className="ft-loginLinkBtn" onClick={openForgotPassword}>
              Forgot password?
            </button>

            <p className="ft-loginPinHelp">
              <span>Forgot your PIN?</span> Contact your dealership manager or
              Flowtrak administrator to have it updated.
            </p>
          </div>

          <div className="ft-loginFooter">
            <div className="ft-loginCopyright">© 2026 Flowtrak</div>
          </div>
        </form>
      </section>

      {fpOpen ? (
        <div
          className="ft-modalOverlay"
          onMouseDown={closeForgotPassword}
          role="presentation"
        >
          <section
            className="ft-modal"
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="forgot-password-title"
            aria-describedby="forgot-password-copy"
          >
            <div className="ft-modalHead">
              <div className="ft-modalTitle" id="forgot-password-title">
                Reset password
              </div>
              <button
                type="button"
                className="ft-modalClose"
                onClick={closeForgotPassword}
                aria-label="Close reset password dialog"
              >
                ×
              </button>
            </div>

            <form className="ft-modalBody" onSubmit={sendPasswordReset}>
              <div className="ft-modalCopy" id="forgot-password-copy">
                Enter your <b>PIN</b> and we’ll send a password reset link to the
                email on file.
              </div>

              <div className="ft-loginField">
                <label className="ft-loginLabel" htmlFor="forgot-password-pin">
                  PIN
                </label>
                <input
                  id="forgot-password-pin"
                  name="forgotPasswordPin"
                  className="ft-loginInput"
                  value={fpPin}
                  onChange={(e) => setFpPin(e.target.value)}
                  inputMode="numeric"
                  autoComplete="username"
                  placeholder="8-digit PIN"
                  required
                  aria-required="true"
                  aria-invalid={!!fpError}
                />
              </div>

              {fpError ? (
                <div className="ft-loginError" role="alert">
                  {fpError}
                </div>
              ) : null}
              {fpStatus ? (
                <div className="ft-modalSuccess" role="status">
                  {fpStatus}
                </div>
              ) : null}

              <div className="ft-modalActions">
                <button
                  type="button"
                  className="ft-btnGhost"
                  onClick={closeForgotPassword}
                  disabled={fpSending}
                >
                  Cancel
                </button>
                <button type="submit" className="ft-btnPrimary" disabled={fpSending}>
                  {fpSending ? "Sending…" : "Send link"}
                </button>
              </div>
            </form>
          </section>
        </div>
      ) : null}

    </main>
  );
}
